<template>
  <div class="gs-checkout-builder-step gs-checkout-form-fields-step">
    <div class="gs-checkout-form-fields-step__toolbar">
      <div>
        <h3>Customer information</h3>
        <p>Core identity fields stay enabled. Add or require only the information you need to fulfill this offer.</p>
      </div>
      <gs-button variant="outline" size="sm" @click="openCustomFieldModal">
        + {{ $t('frontend_product_step5_add_custom') }}
      </gs-button>
    </div>

    <div class="gs-checkout-form-fields-step__grid">
      <article v-for="(formField, index) in localModel.form_fields" :key="`${formField.type}-${index}`"
        class="gs-checkout-form-fields-step__field-card" :class="{ 'is-disabled': !formField.enabled }">
        <div class="gs-checkout-form-fields-step__field-head">
          <gd-checkbox
            v-model="formField.enabled"
            :variant="isCoreField(index) ? 'base': 'primary'"
            :disabled="isCoreField(index)"
            :aria-label="`${formField.enabled ? 'Disable' : 'Enable'} ${formField.label}`"
          />
          <div>
            <strong>{{ formField.label }}</strong>
            <small>{{ isCoreField(index) ? 'Required checkout field' : 'Optional checkout field' }}</small>
          </div>
          <button
            v-if="formField.type == 'custom'"
            type="button"
            class="gs-checkout-form-fields-step__remove"
            :aria-label="`Remove ${formField.label}`"
            @click="openConfirmModal(index)"
          >
            <trash-icon aria-hidden="true" />
          </button>
        </div>
        <div v-if="canSetMandatory(formField) && formField.enabled" class="gs-checkout-form-fields-step__mandatory">
          <span>Required for buyers</span>
          <gd-toggle
            v-model="formField.mandatory"
            :value="formField.mandatory"
            :aria-label="`Require ${formField.label}`"
          />
        </div>
      </article>
    </div>

    <gs-modal v-model="showCustomFieldModal" :title="$t('frontend_groovesell_add_new_custom_field')" size="lg">
      <div class="gs-checkout-form-fields-step__modal-body">
        <gs-field :label="$t('frontend_product_step5_field_name')" hint="This label is shown to buyers at checkout.">
          <text-input v-model="customFieldName" class="w-full" value="customFieldName"
            :placeholder="$t('frontend_product_step5_field_name')" />
        </gs-field>
        <div class="gs-checkout-form-fields-step__modal-actions">
          <gs-button variant="outline" @click="showCustomFieldModal = false">Cancel</gs-button>
          <gs-button variant="primary" :disabled="!customFieldName.trim().length" @click="addCustomField">
            {{ $t('frontend_save') }}
          </gs-button>
        </div>
      </div>
    </gs-modal>
    <gs-alert-dialog
      v-model="showRemoveFormFieldDialog"
      title="Remove custom field?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      cancel-label="Cancel"
      variant="danger"
      @confirm="removeFormField"
    />
  </div>
</template>

<script>
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';

export default {
  name: 'Step5',
  components: {
    TrashIcon
  },
  props: {
    model: {
      required: true,
    },
    product: {
      required: true,
    },
  },
  data() {
    return {
      customFieldName: '',
      showCustomFieldModal: false,
      showRemoveFormFieldDialog: false,
      formFieldId: null,
      localModel: this.model,
      productData: this.product,
      funnel_id: this.$route.params.id,
    };
  },
  validations: {},
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },
  methods: {
    isCoreField(index) {
      return index < 4;
    },
    openCustomFieldModal() {
      this.customFieldName = '';
      this.showCustomFieldModal = true;
    },

    addCustomField() {
      if (!this.customFieldName.length) {
        return false;
      }
      const lastFormFieldOrder = this.localModel.form_fields[this.localModel.form_fields.length - 1].order;
      this.localModel.form_fields.push({
        order: lastFormFieldOrder + 1,
        type: 'custom',
        label: this.customFieldName,
        enabled: 1,
      });

      this.customFieldName = '';
      this.showCustomFieldModal = false;
    },
    openConfirmModal(id) {
      this.formFieldId = id;
      this.showRemoveFormFieldDialog = true;
    },
    async removeFormField() {
      if (this.formFieldId !== null) {
        this.localModel.form_fields.splice(this.formFieldId, 1);
      }
      this.formFieldId = null;
    },
    canSetMandatory(formField) {
      return ![
        'fname',
        'lname',
        'email',
        'countryAndState',
        'address',
        'cityAndPostalCode',
      ].includes(formField.type);
    },
    validate() {
      this.$v.$touch();
      let isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },
  },
};
</script>

<style scoped>
.gs-checkout-form-fields-step__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--gs-border, #e2e8f0);
}

.gs-checkout-form-fields-step h3,
.gs-checkout-form-fields-step p {
  margin: 0;
}

.gs-checkout-form-fields-step h3 {
  color: var(--gs-foreground, #182230);
  font-size: 14px;
  font-weight: 800;
}

.gs-checkout-form-fields-step p {
  max-width: 650px;
  margin-top: 4px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 12px;
  line-height: 1.5;
}

.gs-checkout-form-fields-step__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.gs-checkout-form-fields-step__field-card {
  min-width: 0;
  padding: 14px;
  background: #fff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 10px;
}

.gs-checkout-form-fields-step__field-card.is-disabled {
  background: #f8fafc;
}

.gs-checkout-form-fields-step__field-head {
  display: grid;
  align-items: center;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
}

.gs-checkout-form-fields-step__field-head strong,
.gs-checkout-form-fields-step__field-head small {
  display: block;
}

.gs-checkout-form-fields-step__field-head strong {
  overflow: hidden;
  color: var(--gs-foreground, #182230);
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-checkout-form-fields-step__field-head small {
  margin-top: 2px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 10px;
}

.gs-checkout-form-fields-step__remove {
  display: grid;
  width: 30px;
  height: 30px;
  padding: 0;
  color: var(--gs-danger, #dc2626);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 7px;
  place-items: center;
}

.gs-checkout-form-fields-step__remove:hover,
.gs-checkout-form-fields-step__remove:focus-visible {
  background: #fff1f2;
  border-color: var(--gs-danger-border, #fecaca);
  outline: none;
}

.gs-checkout-form-fields-step__remove svg {
  width: 15px;
  height: 15px;
  fill: currentColor;
}

.gs-checkout-form-fields-step__mandatory {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  border-top: 1px solid var(--gs-border, #e2e8f0);
}

.gs-checkout-form-fields-step__modal-body {
  padding: 22px;
}

.gs-checkout-form-fields-step__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
}
</style>
